import { SingleVideoSchema } from "@/types/video-tutorials";
import { MinusIcon, PlusIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { ConvertToPersianNumbers } from "@/utils/utils";
import { Icon } from "@iconify/react";
import { Button } from "@/components/button";
import { IconButton } from "@/components/icon-button";

type SubsectionGroup = {
  subsection: string;
  videos: SingleVideoSchema[];
};

type VisibleVideoItem = SingleVideoSchema | SubsectionGroup;

type Props = {
  videos: SingleVideoSchema[];
  sectionId?: number;
  headerTitle: string;
  showBySubsections?: boolean;
};

const isSubsectionGroup = (item: VisibleVideoItem): item is SubsectionGroup =>
  !("content_id" in item);

export default function VideosList({
  videos,
  sectionId,
  headerTitle,
  showBySubsections = false,
}: Readonly<Props>) {
  const [showAll, setShowAll] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const handleVideosReadTime = () => {
    let totalReadTime = 0;

    if (!videos?.length) {
      return 0;
    }

    videos?.forEach(v => totalReadTime += v.read_time)

    return totalReadTime;
  }

  const groupedEducations = Object.values(
    videos.reduce((acc, education) => {
      const subsection = education.subsection;

      if (!acc[subsection]) {
        acc[subsection] = {
          subsection,
          videos: [],
        };
      }

      acc[subsection].videos.push(education);

      return acc;
    }, {} as Record<string, { subsection: string; videos: typeof videos }>)
  );

  const MAX_VISIBLE = 4;
  const hasMore = groupedEducations.length > MAX_VISIBLE;
  const sourceList: VisibleVideoItem[] = showBySubsections ? groupedEducations : videos;
  const visibleVideos = showAll ? sourceList : sourceList.slice(0, MAX_VISIBLE + 1);

  return (
    <section className="relative w-full bg-white rounded-2xl p-6 shadow-[0px_8px_40px_rgba(0,0,0,0.1)] select-none">
      <div
        className="flex items-center justify-between w-full"
      >
        {
          sectionId ?
            <Link
              href={`/video-tutorials/role/${sectionId}`}
              className="text-lg font-semibold text-neutral"
            >
              {headerTitle}
            </Link>
            :
            <b className="text-lg font-semibold text-neutral cursor-default">{headerTitle}</b>
        }

        <div className="flex items-center justify-end gap-6">
          {
            isOpen ?
              null
              :
              <div className="flex items-center justify-center gap-4 text-base text-[#98A2B3]">
                <div className="flex items-center justify-center gap-1">
                  <Icon icon="hugeicons:computer-video" color="#98A2B3" width={16} height={16} />
                  {ConvertToPersianNumbers(videos?.length)} ویدئو
                </div>
                <div className="h-4 border border-[#98A2B3]"></div>
                <div className="flex items-center justify-center gap-1">
                  <Icon icon="hugeicons:clock-01" color="#98A2B3" width={16} height={16} />
                  {ConvertToPersianNumbers(handleVideosReadTime())} دقیقه
                </div>
              </div>
          }

          <IconButton
            variant="bare"
            ariaLabel={isOpen ? "بستن لیست" : "باز کردن لیست"}
            onClick={() => setIsOpen(pre => !pre)}
            className={`
            flex items-center justify-center w-5 h-5 md:w-8 md:h-8 rounded-full self-start cursor-pointer
            ${isOpen ? "bg-primary" : "bg-white"}
            `}
          >
            {isOpen ? (
              <MinusIcon className="text-white h-3 w-3 md:h-5 md:w-5" />
            ) : (
              <PlusIcon className="text-[#6F6C90] h-3 w-3 md:h-7 md:w-7" />
            )}
          </IconButton>
        </div>
      </div>

      {isOpen && (
        <div className="flex gap-2 mt-4">

          {/* circles */}
          <div className="flex flex-col justify-between items-center w-10 relative">
            <div className="absolute right-1/2 translate-x-1/2 w-0.5 rounded-lg bg-primary z-1 top-8 bottom-8"></div>
            {visibleVideos.map((_, index) => (
              <div
                key={index + "-circle"}
                className="h-16 flex items-center justify-center w-8"
              >
                <span className="size-8 text-white bg-primary inline-flex items-center justify-center rounded-full border-2 border-blue-400 cursor-default z-2">
                  {index + 1}
                </span>
              </div>
            ))}
          </div>

          {/* list */}
          <div className="flex-1 flex flex-col">
            {visibleVideos.map((video, index) => {
              const href = isSubsectionGroup(video)
                ? `/video-tutorials/role/${sectionId ?? video.videos[0]?.section?.id}`
                : `/video-tutorials/view/${video.content_id}`;
              const label = isSubsectionGroup(video) ? video.subsection : video.title;
              const itemKey = isSubsectionGroup(video) ? video.subsection : video.content_id;

              return (
                <div
                  key={itemKey}
                  className={`relative flex items-center justify-between h-16 px-2 ${index < visibleVideos.length - 1 ? "border-b-2 border-section" : ""}`}
                >
                  <Link
                    href={href}
                    className={`text-ordinary cursor-pointer`}
                  >
                    {label}
                  </Link>

                  <div className="flex gap-6">
                    <Link
                      href={href}
                      className="text-blue-600"
                    >
                      مشاهده
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Fade Overlay */}
          {hasMore && !showAll && (
            <div className="absolute inset-x-0 bottom-0 h-21.5 flex justify-center items-end gap-2 bg-linear-to-b from- to-white">
              <Button
                variant="bare"
                type="button"
                text="مشاهده بیشتر"
                onClick={() => setShowAll(true)}
                className="p-4 text-base text-brand-700 cursor-pointer"
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}